/**
 * Photo-set API client (Anshul, 2026-09-26): a photo set is its own item in a project's
 * Videos list ("kind": "photos"), created empty, filled with images in batches, then
 * completed -- after which its run, findings, review queue, assessment and report all go
 * through the same routes every other video kind already uses (videosApi.js,
 * findingsApi.js, assessmentApi.js), so only the photo-specific create/upload/complete/list
 * routes get a client of their own here.
 *
 * A new module rather than an addition to services/videosApi.js: that file is owned by a
 * different agent in this same wave (this phase's YOUR FILES list grants only
 * services/photosApi.js), and every other phase module in this client already gets its own
 * file (standardsApi.js, videosApi.js, findingsApi.js) rather than growing one shared file.
 */
import api from './api';

export const photosAPI = {
  // POST /projects/{project_id}/photo-sets {title} -> 201 {video} (kind "photos", status
  // "UPLOADING"). Mirrors videosAPI.prepareUpload's two-phase shape: this call only creates
  // the row so the caller has an id to upload photos against.
  createSet: (projectId, body) => api.post(`/api/v1/projects/${projectId}/photo-sets`, body),

  // POST /videos/{video_id}/photos, multipart field "files" (at most 20 per request per the
  // server's own limit -- this client uploads in batches of 10, PhotoUpload.js's own choice,
  // comfortably under that ceiling) -> 200 {photo_count, accepted, rejected}; 400 when every
  // file in the request was rejected. `onUploadProgress` and `options` (e.g. `{ signal }` to
  // cancel) are wired straight through, the same convention as videosApi.uploadFileForVideo.
  upload: (videoId, formData, onUploadProgress, options = {}) =>
    api.post(`/api/v1/videos/${videoId}/photos`, formData, { onUploadProgress, ...options }),

  // POST /videos/{video_id}/photos/complete -> 200 {video} (status "READY",
  // frame_count = number of photos). Marks the set finished so Start/process can run
  // against it like any other READY video.
  complete: (videoId) => api.post(`/api/v1/videos/${videoId}/photos/complete`),

  // GET /videos/{video_id}/photos -> 200 {photos: [{index, url}]}, index 1-based.
  list: (videoId) => api.get(`/api/v1/videos/${videoId}/photos`),
};

export default photosAPI;
